import { useCallback, useState } from "react"
import { useAuthUser, useHasFeatureFlag } from "../Auth"
import { ErrorBoundary } from "@sembark-travel/ui-error-boundary"
import { Dialog } from "@sembark-travel/ui-dialog"
import {
	Box,
	Button,
	CheckboxInput,
	Component,
	Divider,
	Icons,
	Inline,
	Spinner,
	Stack,
	TabContent,
	TabItem,
	Tabs,
	TabsList,
	Text,
} from "@sembark-travel/ui-base"
import {
	CheckboxInputField,
	Form,
	SelectField,
	SubmissionError,
	TextInputField,
	withServerErrors,
} from "@sembark-travel/ui-form"
import { Link } from "@sembark-travel/ui-router"
import { generatePath } from "../router-utils"
import { ITrip } from "./store"
import { useXHR } from "@sembark-travel/xhr"
import { IComment } from "../Comments"
import {
	formatDate,
	localOrUtcTimestampToLocalDate,
} from "@sembark-travel/datetime-utils"
import { ShareViaWhatsApp } from "@sembark-travel/ui-share"
import {
	CopyToClipboard,
	CopyToClipboardButton,
} from "@sembark-travel/ui-copy-to-clipboard"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { EmailContent } from "../shared/EmailContent"
import { SelectOutboundMailbox } from "../Integrations"
import useSWR from "swr"

type TShareVia = "whatsapp" | "email"

export function ShareTripToDMCInDialog({
	trip,
	isValidating,
	children,
}: {
	trip: ITrip
	isValidating?: boolean
	children: (props: { onClick: () => void }) => React.ReactNode
}) {
	const [activeTab, setActiveTab] = useState<undefined | TShareVia>(undefined)
	const isTripEnquiryProcessEnabled = useHasFeatureFlag("trip_enquiry_process")

	if (!isTripEnquiryProcessEnabled) {
		return null
	}

	return (
		<>
			{children({ onClick: () => setActiveTab("whatsapp") })}

			<Dialog
				open={Boolean(activeTab)}
				lg
				onClose={() => setActiveTab(undefined)}
				title="Share Enquiry"
			>
				<ErrorBoundary>
					<ShareAgentToDMC
						activeTab={activeTab}
						setActiveTab={setActiveTab}
						trip={trip}
						isValidating={isValidating}
					/>
				</ErrorBoundary>
			</Dialog>
		</>
	)
}

function OptionsBar({
	options,
	isValidating,
}: {
	options: Array<{
		id: string
		label: string
		checked: boolean
		onChange: () => void
	}>
	isValidating?: boolean
}) {
	if (options.length === 0) {
		return null
	}

	return (
		<Box
			position="sticky"
			top="0"
			padding="4"
			zIndex="10"
			bgColor="subtle"
			borderBottomWidth="1"
			boxShadow="sm"
		>
			<Inline flexWrap="wrap" gap="4">
				{options.map((option) => (
					<Inline key={option.id} as="label" gap={"2"}>
						<CheckboxInput
							checked={option.checked}
							onChange={option.onChange}
						/>{" "}
						{option.label}
					</Inline>
				))}
				{isValidating && (
					<Box>
						<Icons.Refresh spin />
					</Box>
				)}
			</Inline>
		</Box>
	)
}

export function ShareAgentToDMC({
	activeTab,
	setActiveTab,
	trip,
	isValidating,
}: {
	activeTab: undefined | TShareVia
	setActiveTab: (activeTab: TShareVia | undefined) => void
	trip: ITrip
	isValidating?: boolean
}) {
	const xhr = useXHR()
	const { user } = useAuthUser()
	const [emailSubject, setEmailSubject] = useState("")
	const [hasEmailSignature, setHasEmailSignature] = useState<boolean>(false)
	const {
		data: commentsData,
		isLoading: isCommentsLoading,
		isValidating: isCommentsValidating,
	} = useSWR<{
		data: IComment[]
	}>(`/trips/${trip.id}/comments`, () =>
		xhr.get(`/trips/${trip.id}/comments`).then((resp) => resp.data)
	)
	const comments: IComment[] | undefined = commentsData?.data || undefined

	const fetchEmailContent = useCallback(
		async (params: object) => {
			return await xhr
				.get<{
					subject: string
					content: string
					options: Array<{ name: string; included: 1 | 0 }>
					has_email_signature?: boolean
				}>(`/trips/${trip.id}/request-enquiry-as-email`, {
					params,
				})
				.then((resp) => {
					setEmailSubject(resp.data.subject)
					setHasEmailSignature(Boolean(resp.data.has_email_signature))
					return resp.data
				})
		},
		[trip.id, xhr]
	)

	if (isCommentsLoading || isCommentsValidating) {
		return <Spinner alignCenter padding="4" />
	}

	const baseWhatsappContent = `Dear Sir / Ma'am,

Greetings from *${trip.tenant_brand?.short_name || user?.tenant?.short_name}*!!!

We are looking to arrange a travel package for our guests. Kindly share a suitable proposal based on details outlined below at the earliest.

----------
*Package Requirements (Trip ID: ${trip.branded_id})*
----------

*Destination: ${trip.destinations.map((d) => d.name).join(", ")}*

*Start Date: ${formatDate(
		localOrUtcTimestampToLocalDate(trip.start_date_local, trip.start_date),
		"DD MMM, YYYY"
	)}*

*Trip Duration: ${trip.nights} Nights, ${trip.days} Days*

*Pax: ${trip.pax_string}*

`
	return (
		<Tabs layout="col">
			<TabsList>
				<TabItem
					active={activeTab === "whatsapp"}
					onClick={() => setActiveTab("whatsapp")}
				>
					<Icons.Annotation /> WhatsApp
				</TabItem>
				<TabItem
					active={activeTab === "email"}
					onClick={() => setActiveTab("email")}
				>
					<Icons.Mail /> Email
				</TabItem>
			</TabsList>

			<TabContent active={activeTab === "whatsapp"}>
				<Component
					initialState={{
						includeLatestComment: comments ? comments.length > 0 : false,
					}}
				>
					{({ state, setState }) => {
						const whatsappContent = `${baseWhatsappContent}${
							state.includeLatestComment && comments && comments.length > 0
								? `\n_*Note*_\n------\n${comments[0].body}\n------`
								: ""
						}`

						return (
							<Box position="relative">
								<Box paddingX="4" paddingY="2">
									<Text fontSize="sm">
										<Icons.LightBulb opacity="70" /> Use toggles to customize
										the content according to your needs.
									</Text>
								</Box>
								<OptionsBar
									options={[
										...(comments && comments.length > 0
											? [
													{
														id: "latest-comment",
														label: "Latest Followup",
														checked: state.includeLatestComment,
														onChange: () =>
															setState({
																...state,
																includeLatestComment:
																	!state.includeLatestComment,
															}),
													},
												]
											: []),
									]}
									isValidating={isValidating}
								/>
								<Box roundedBottom="md" overflow="hidden">
									<ShareViaWhatsApp
										text={whatsappContent}
										trackingContentType={"trip"}
										trackingContentId={"trip_details"}
										disabled={isValidating}
									/>
								</Box>
							</Box>
						)
					}}
				</Component>
			</TabContent>

			<TabContent active={activeTab === "email"} position="relative">
				<Box style={{ minHeight: "90vh" }}>
					<Stack gap="3">
						<Box
							paddingX="4"
							paddingY="2"
							borderWidth={"1"}
							backgroundColor={"subtle"}
						>
							<Text as="p" fontSize="sm" color="muted">
								<Icons.LightBulb /> Copy the subject and body or send the email
								directly using one of your connected mailboxes.
							</Text>
						</Box>
						<CopyToClipboard<HTMLDivElement>>
							{({ nodeToCopy, copyNodeTextToClipboard }) => (
								<Stack paddingX="4" gap="1">
									<Inline justifyContent="between" alignItems="center">
										<Text fontWeight="semibold">Subject</Text>
										<CopyToClipboardButton
											size="sm"
											level="primary"
											onClick={async () => {
												return copyNodeTextToClipboard()
											}}
										>
											<Icons.ClipboardCopy /> Copy Subject
										</CopyToClipboardButton>
									</Inline>
									<Box
										paddingY="2"
										paddingX="4"
										borderWidth="1"
										rounded="md"
										bgColor="subtle"
									>
										<Box ref={nodeToCopy}>{emailSubject || "..."}</Box>
									</Box>
								</Stack>
							)}
						</CopyToClipboard>
						<Box>
							<Box marginLeft="4" paddingY={"1"}>
								<Text fontWeight="semibold">Body</Text>
							</Box>
							<EmailContent
								paddingY="4"
								copyTrackId="trip_dmc_request_enquiry"
								contentCacheKey={`/trips/${trip.id}/request-enquiry-as-email`}
								fetchContent={fetchEmailContent}
								actions={({ inclusions, exclusions }) => (
									<SendTripEnquiryEmailDialog
										tripId={trip.id}
										inclusions={inclusions}
										exclusions={exclusions}
										hasEmailSignature={hasEmailSignature}
									/>
								)}
								defaultInclusionOptions={[
									{
										name: "Latest Followup",
										included: comments && comments.length > 0 ? 1 : 0,
									},
								]}
							/>
						</Box>
					</Stack>
				</Box>
			</TabContent>
		</Tabs>
	)
}

function SendTripEnquiryEmailDialog({
	tripId,
	inclusions,
	exclusions,
	hasEmailSignature,
}: {
	tripId: number
	inclusions: string | null
	exclusions: string | null
	hasEmailSignature?: boolean
}) {
	const xhr = useXHR()
	const { user } = useAuthUser()
	const [isOpen, setIsOpen] = useState(false)
	const hasConnectedOutboundMailboxes =
		(user?.outbound_mailboxes_count || 0) > 0

	if (!hasConnectedOutboundMailboxes) {
		return null
	}

	return (
		<>
			<Button size="sm" level="primary" onClick={() => setIsOpen(true)}>
				<Icons.Mail /> Send Email
			</Button>
			<Dialog
				open={isOpen}
				onClose={() => setIsOpen(false)}
				title="Send Enquiry Email"
			>
				<Dialog.Body>
					<Stack gap="4">
						<Text fontSize="sm" color="muted">
							Choose the connected mailbox and recipient details before sending
							this enquiry.
						</Text>
						<Form<{
							mailbox: { id: number; name: string; email: string } | null
							to_email: string
							to_name: string
							cc: string
							include_signature: boolean
						}>
							initialValues={{
								mailbox: null,
								to_email: "",
								to_name: "",
								cc: "",
								include_signature: hasEmailSignature,
							}}
							onSubmit={withServerErrors(async (values) => {
								await xhr.post(`/trips/${tripId}/send-request-enquiry-email`, {
									mailbox: values.mailbox?.id,
									to_email: values.to_email,
									to_name: values.to_name || null,
									cc: values.cc,
									include_signature: values.include_signature,
									inclusions,
									exclusions,
								})
								showSnackbar("Enquiry email sent successfully.")
								setIsOpen(false)
							})}
							subscription={{ submitting: true }}
						>
							{({ handleSubmit, submitting }) => (
								<form noValidate onSubmit={handleSubmit}>
									<Stack gap="4">
										<SelectField
											name="mailbox"
											label="Send From"
											select={SelectOutboundMailbox}
											required
											disabled={submitting}
										/>
										<TextInputField
											name="to_email"
											label="B2B Source Email"
											type="email"
											required
											placeholder="e.g. dmc@travel.com"
											disabled={submitting}
										/>
										<TextInputField
											name="to_name"
											label="B2B Source Name"
											disabled={submitting}
											placeholder="e.g. Travel B2B Source"
										/>
										<TextInputField
											name="cc"
											label="Cc"
											placeholder="email@example.com, other@example.com"
											help={({ value }) =>
												"Comma-separated email addresses to keep in the loop. " +
												(value
													? "<" +
														String(value)
															.split(",")
															.map((e) => String(e).trim())
															.filter(Boolean)
															.join(">,<") +
														">"
													: "")
											}
											disabled={submitting}
										/>
										<Stack gap="1">
											<CheckboxInputField
												name="include_signature"
												label="Include my email signature"
												disabled={submitting || !hasEmailSignature}
											/>
											{!hasEmailSignature ? (
												<Text fontSize="sm" color="muted">
													You have no email signature yet.{" "}
													<Link
														to={`${generatePath("/settings")}#email-signature`}
														anchored
														color="accent"
													>
														Set up your email signature
													</Link>
													.
												</Text>
											) : null}
										</Stack>
										<Divider marginY="0" />
										<SubmissionError />
										<Inline gap="4">
											<Button type="submit" disabled={submitting}>
												<Icons.Mail />{" "}
												{submitting ? "Sending..." : "Send Email"}
											</Button>
											<Button
												type="button"
												level="tertiary"
												onClick={() => setIsOpen(false)}
												disabled={submitting}
											>
												Cancel
											</Button>
										</Inline>
									</Stack>
								</form>
							)}
						</Form>
					</Stack>
				</Dialog.Body>
			</Dialog>
		</>
	)
}
